// filters-helpers.ts
import { SelectOption } from 'src/services/base-model'

// Mapped type to convert filter properties for UI state management.
export type UiFilterState<T> = {
  [K in keyof T]: T[K] extends string | undefined | null
    ? SelectOption[]
    : T[K] extends string[] | undefined | null
    ? SelectOption[]
    : T[K]
}

// Helper to create the initial UI state.
export function createInitialUiState<T extends object>(
  initialValues: Partial<T>,
): UiFilterState<Partial<T>> {
  const uiState = {} as UiFilterState<Partial<T>>
  for (const key in initialValues) {
    const initialValue = initialValues[key as keyof T]
    if (typeof initialValue === 'boolean' || typeof initialValue === 'number') {
      // For booleans and numbers, keep the original value
      uiState[key as keyof T] = initialValue as any
    } else {
      // For strings and arrays, initialize as an empty array
      uiState[key as keyof T] = [] as any
    }
  }
  return uiState
}

export function convertSingleUiFilterToApi<T extends object, K extends keyof T>(
  uiValue: UiFilterState<Partial<T>>[K],
  initialValue: Partial<T>[K],
) {
  if (Array.isArray(uiValue)) {
    if (Array.isArray(initialValue)) {
      // Multi-select: T[K] is string[], so API needs string[]
      return (uiValue as SelectOption[]).map((opt) => opt.value) as any
    } else {
      // Single-select: T[K] is string, so API needs a single string or undefined
      return uiValue.length > 0 ? (uiValue[0] as SelectOption).value : undefined
    }
  }
  // For booleans, numbers, and other non-array types, return as-is
  return uiValue as any
}
